import React, { useState } from 'react';
import { Modal, Form, Input, Button, Upload, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { oneKlicks } from '../../SDK/oneclick';
import { OneKlicksFileSDK } from '../../SDK/OneKlicksFileSDK';
import CryptoJS from 'crypto-js';

const AddHospitals = ({ closeModal, getHospitals }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [fileList, setFileList] = useState([]);

    const handleSubmit = async (values) => {
        try {
            setLoading(true);
            
            // Generate a random password for the hospital
            const randomPassword = Math.random().toString(36).slice(-8) + Math.random().toString(36).slice(-8);
            const hashedPassword = CryptoJS.SHA256(randomPassword).toString();

            // Upload profile photo if exists
            let profile_photo = '';
            let profile_url = '';
            if (fileList.length > 0) {
                const file = fileList[0];
                console.log('📁 Uploading file:', file.name, file.size);
                
                try {
                    const fileResponse = await OneKlicksFileSDK.uploadFile('hospitals', 'public', file);
                    console.log('📁 Full fileResponse:', JSON.stringify(fileResponse, null, 2));
                    
                    if (fileResponse && fileResponse.success) {
                        // Store the file ID for later use - check multiple possible response structures
                        profile_photo = fileResponse.id || fileResponse.fileid || fileResponse.file?.id || fileResponse.fileId;
                        console.log('📁 Profile photo ID:', profile_photo);
                        console.log('📁 Full fileResponse structure:', Object.keys(fileResponse));
                        
                        // Try to get the file URL if we have an ID
                        if (profile_photo) {
                            try {
                                const fileUrlResponse = await OneKlicksFileSDK.getfile(profile_photo);
                                console.log('📁 fileUrlResponse:', fileUrlResponse);
                                if (fileUrlResponse && fileUrlResponse.success) {
                                    profile_url = fileUrlResponse.fileUrl;
                                    console.log('📁 Profile URL:', profile_url);
                                }
                            } catch (error) {
                                console.error('❌ Error getting file URL:', error);
                                // Continue without the file URL - we can get it later when needed
                            }
                        } else {
                            console.warn('⚠️ No file ID found in response:', fileResponse);
                        }
                    } else {
                        console.error('❌ File upload failed:', fileResponse);
                        message.error('Failed to upload image. Please try again.');
                    }
                } catch (uploadError) {
                    console.error('❌ Error uploading file:', uploadError);
                    message.error('Failed to upload image: ' + uploadError.message);
                }
            }

            // Create hospital in database
            const tableSchema = '';
            const tableName = 'users';
            const conditions = {
                email: values.email,
                phone: values.phone,
            };
            const hospitalData = {
                display_name: values.name,
                name: values.name,
                email: values.email,
                phone: values.phone,
                address: values.address,
                longitude: values.longitude || '',
                latitude: values.latitude || '',
                description: values.description || '',
                country: values.country,
                user_type: 'hospitals',
                status: 1,
                pass: hashedPassword,
                profile_url: profile_url,
                profile_photo: profile_photo,
                profile_photo_url: profile_url, // Add this field too
                profile_image: profile_photo, // Add this field too
                // Additional fields for hospital
                url: '',
            };

            console.log('🏥 About to create hospital with data:', {
                tableSchema,
                tableName,
                hospitalData,
                conditions
            });
            console.log('📁 Image data:', {
                profile_photo,
                profile_url,
                hasImage: fileList.length > 0
            });
            
            const response = await oneKlicks.create(tableSchema, tableName, hospitalData, conditions);
            console.log('Create hospital response:', response);
            
            if (response && response.success) {
                console.log('✅ Hospital creation successful! Response:', response);
                
                // Verify the hospital was actually saved by fetching it back
                try {
                    const verifyTableSchema = '';
                    const verifyTableName = 'users';
                    const verifyConditions = {
                        email: values.email,
                        user_type: 'hospitals'
                    };
                    const verifyOrderBy = '';
                    const verifySearch = {};
                    const verifyPagination = {};
                    const verifyMargedata = [];
                    
                    console.log('🔍 Verifying hospital in database with conditions:', verifyConditions);
                    
                    const verifyResponse = await oneKlicks.read(
                        verifyTableSchema, 
                        verifyTableName, 
                        verifyConditions, 
                        verifyOrderBy, 
                        verifySearch, 
                        verifyPagination, 
                        verifyMargedata
                    );
                    
                    console.log('🔍 Verification response:', verifyResponse);
                    
                    if (verifyResponse && verifyResponse.data && verifyResponse.data.length > 0) {
                        const savedHospital = verifyResponse.data[0];
                        console.log('✅ Hospital verified in database:', savedHospital);
                        message.success(`Hospital added successfully! ID: ${savedHospital.id}`);
                    } else {
                        console.warn('⚠️ Hospital not found in database after creation');
                        message.warning('Hospital creation reported success but not found in database');
                    }
                } catch (verifyError) {
                    console.error('❌ Error verifying hospital in database:', verifyError);
                    message.warning('Hospital creation reported success but verification failed');
                }
                
                form.resetFields();
                setFileList([]);
                closeModal();
                getHospitals();
            } else {
                console.error('❌ Create hospital failed:', response);
                message.error(`Failed to add hospital: ${response?.message || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('Error adding hospital:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
            message.error(`Failed to add hospital: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const uploadProps = {
        onRemove: () => {
            setFileList([]);
        },
        beforeUpload: (file) => {
            console.log('📁 File selected for upload:', file.name, file.size, file.type);
            setFileList([file]);
            return false;
        },
        fileList,
    };

    return (
        <Modal
            title="Add New Hospital"
            open={true}
            onCancel={closeModal}
            footer={null}
            width={800}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
            >
                <Form.Item
                    name="name"
                    label="Hospital Name"
                    rules={[{ required: true, message: 'Please enter hospital name' }]}
                >
                    <Input placeholder="Enter hospital name" />
                </Form.Item>

                <Form.Item
                    name="email"
                    label="Email"
                    rules={[
                        { required: true, message: 'Please enter email' },
                        { type: 'email', message: 'Please enter a valid email' }
                    ]}
                >
                    <Input placeholder="Enter email" />
                </Form.Item>

                <Form.Item
                    name="phone"
                    label="Phone"
                    rules={[{ required: true, message: 'Please enter phone number' }]}
                >
                    <Input placeholder="Enter phone number" />
                </Form.Item>

                <Form.Item
                    name="address"
                    label="Address"
                    rules={[{ required: true, message: 'Please enter address' }]}
                >
                    <Input.TextArea rows={4} placeholder="Enter address" />
                </Form.Item>

                <Form.Item
                    name="longitude"
                    label="Longitude"
                >
                    <Input placeholder="Enter longitude" />
                </Form.Item>

                <Form.Item
                    name="latitude"
                    label="Latitude"
                >
                    <Input placeholder="Enter latitude" />
                </Form.Item>

                <Form.Item
                    name="description"
                    label="Description"
                >
                    <Input.TextArea rows={4} placeholder="Enter description" />
                </Form.Item>

                <Form.Item
                    name="country"
                    label="Country"
                    rules={[{ required: true, message: 'Please enter country' }]}
                >
                    <Input placeholder="Enter country" />
                </Form.Item>

                <Form.Item
                    label="Profile Photo"
                >
                    <Upload {...uploadProps} maxCount={1}>
                        <Button icon={<UploadOutlined />}>Upload Photo</Button>
                    </Upload>
                </Form.Item>

                <Form.Item>
                    <div className="d-flex justify-content-end" style={{ gap: '10px' }}>
                        <Button onClick={closeModal}>
                            Cancel
                        </Button>
                        <Button type="primary" htmlType="submit" loading={loading}>
                            Add Hospital
                        </Button>
                    </div>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default AddHospitals; 
